import classNames from 'classnames'
import { useCallback, useEffect, useState } from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { EXPERIMENTS } from '@src/experiments'
import { SliderImageWithText } from '@src/quince-components/src'
import { BreadcrumbSchema } from '@src/quince-components/src/atoms/breadCrumb'
import { PRODUCT_CATALOG_ID_TO_ICON_MAP } from '@src/quince-site-common/src/constants/awardLogos'
import Directory from '@src/quince-site-common/src/domains/Directory'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductHierarchy from '@src/quince-site-common/src/domains/ProductHierarchy'
import ProductMedia from '@src/quince-site-common/src/domains/ProductMedia'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import ProductVariant from '@src/quince-site-common/src/domains/ProductVariant'
import { getColorFromSelectedOptions } from '@src/quince-site-common/src/helpers/productHelper'
import useExperimentBucket from '@src/quince-site-common/src/hooks/useExperimentBucket'
import { useWishList } from '@src/quince-site-common/src/hooks/useWishList'

import ProductPageSectionProps from '../../../domains/ProductPageSectionProps'
import useProductPageContext from '../../../hooks/useProductContext'

import { useProductImages } from './hooks/use-product-images'
import styles from './productImages.module.scss'

const ProductImages = (props: ProductPageSectionProps) => {
  const { sectionData } = props

  const productCatalogId = props.product.id
  const houseKeepingExpBucket = useExperimentBucket(EXPERIMENTS.HOUSE_KEEPING_AWARD.experimentName)
  const shouldShowHouseKeepingLogo = houseKeepingExpBucket === EXPERIMENTS.HOUSE_KEEPING_AWARD.bucketNames.show
  const getTopLeftIconSVG = useCallback(
    ({ index }: { index: number }) =>
      index === 0 && shouldShowHouseKeepingLogo && PRODUCT_CATALOG_ID_TO_ICON_MAP[productCatalogId]
        ? PRODUCT_CATALOG_ID_TO_ICON_MAP[productCatalogId]
        : null,
    [productCatalogId, shouldShowHouseKeepingLogo],
  )

  const {
    productPageExpData: {
      showSmallProductImage,
      productImageClass = '',
      productImageLeftContainerClass = '',
      productImageLeftContainerSliderClass,
      hideProductThumbnails = false,
      imageContainerClassName = '',
      hideBreadCrumb,
      sliderIconClassName = '',
      breadCrumbListClass = '',
      breadCrumbContainerHeaderClass = '',
      productImageRightContainerClass = '',
      sliderImageClass = '',
    },
  } = useProductPageContext()

  const { selectedOptions, directory, productMedia, productTags } = useProductImages(props.product, sectionData)

  const [isWishListed, setIsWishListed] = useState(false)
  const { isItemInWishList, addItemToWishList, removeItemFromWishList } = useWishList()
  useEffect(() => {
    setIsWishListed(isItemInWishList(props.product, selectedOptions))
  }, [props.product.id, selectedOptions])

  const onWishlistToggle = async () => {
    setIsWishListed(!isWishListed)
    if (isWishListed) {
      await removeItemFromWishList(props.product, selectedOptions)
    } else {
      await addItemToWishList(props.product, selectedOptions, 'pdp_page')
    }
  }

  //Note: Hacky script logic for Images has been removed in SSR
  // TODO: Remove this code after full SSR rollout
  // const [renderHackyProductImages, setRenderHackyProductImages] = useState(
  //   !isBrowser() || !window.__client_side_navigation__,
  // )
  // useEffect(() => {
  //   setRenderHackyProductImages(false)
  // }, [])

  return (
    <>
      <BreadcrumbSchema directory={directory} />
      {renderProductImages({
        productSlug: props.product.slug,
        productMedia: productMedia,
        isWishListed: isWishListed,
        onWishlistToggle: onWishlistToggle,
        productTitle: props.product.title,
        productHierarchy: props.product.hierarchy,
        productTags: productTags,
        hasSelectedOptionImages: true,
        getTopLeftIconSVG: getTopLeftIconSVG,
        showSmallProductImage,
        productImageClass,
        productImageLeftContainerClass,
        productImageLeftContainerSliderClass,
        hideProductThumbnails,
        imageContainerClassName,
        hideBreadCrumb,
        sliderIconClassName,
        breadCrumbListClass,
        breadCrumbContainerHeaderClass,
        productImageRightContainerClass,
        sliderImageClass,
        variants: props.product.variants,
        directory,
        product: props?.product,
        pdpHeroImageId: `${TEST_LOCATORS.SLIDER_IMAGES_MAIN_IMAGE}_${getColorFromSelectedOptions(selectedOptions)}`,
      })}
    </>
  )

  // Deprecated code for SSR hacky script
  // // Note: Handling SSR hacky way, where rendering all the product images combinations
  // // This will ensure we have all color combinations available before hydration, to show correct product image quickly, without waiting for hydration to complete.
  // // To ensure performance is not degraded, we are only rendering first two image of each to avoid heavy dom.
  // const allColors = Object.keys(colorValueProductImagesMap).map(color => ({
  //   sanitizedColor: getKebabCaseURLFromInputString(getURLStringFromInputString(color)),
  //   color,
  // }))
  // const allSanitizedColorColorsSet = new Set(allColors.map(({ sanitizedColor }) => sanitizedColor))

  // return (
  //   <div className={styles.image_hacky_container_wrapper}>
  //     <BreadcrumbSchema directory={directory} />
  //     {allColors.map(({ color, sanitizedColor }, index) => (
  //       <div
  //         suppressHydrationWarning
  //         className={getProductAllImageWrapperClassNames(sanitizedColor, index, allSanitizedColorColorsSet)}
  //         key={sanitizedColor}
  //       >
  //         {renderProductImages({
  //           productSlug: props.product.slug,
  //           productMedia: colorValueProductImagesMap[color],
  //           isWishListed: isWishListed,
  //           onWishlistToggle: onWishlistToggle,
  //           productTitle: props.product.title,
  //           productHierarchy: props.product.hierarchy,
  //           productTags: [],
  //           hasSelectedOptionImages: false,
  //           getTopLeftIconSVG: getTopLeftIconSVG,
  //           showSmallProductImage,
  //           productImageClass,
  //           productImageLeftContainerClass,
  //           productImageLeftContainerSliderClass,
  //           hideProductThumbnails,
  //           imageContainerClassName,
  //           hideBreadCrumb,
  //           sliderIconClassName,
  //           breadCrumbListClass,
  //           breadCrumbContainerHeaderClass,
  //           productImageRightContainerClass,
  //           sliderImageClass,
  //           variants: props.product.variants,
  //           directory,
  //           product: props?.product,
  //           pdpHeroImageId: `${TEST_LOCATORS.SLIDER_IMAGES_MAIN_IMAGE}_${color}`,
  //         })}
  //       </div>
  //     ))}
  //     {renderHackyProductImages && (
  //       <div
  //         style={{ display: 'none' }}
  //         dangerouslySetInnerHTML={{
  //           __html: `<img src
  //                 onerror="window?.quinceUMD?.adHoc?.api?.loadPdpImages && window?.quinceUMD?.adHoc?.api?.loadPdpImages()"
  //                 />`,
  //         }}
  //       />
  //     )}
  //   </div>
  // )
}

// const getProductAllImageWrapperClassNames = (
//   currentProductSanitizedColor: string,
//   index: number,
//   allSanitizedColorSet: Set<string>,
// ) => {
//   let className = `hack_pdp_hero_image ${currentProductSanitizedColor}`

//   const queryParams = isBrowser() ? queryString.parse(window.location.search) : undefined
//   const colorInSearchParams = queryParams?.color as string | undefined
//   const sanitizedColorInSearchParams =
//     colorInSearchParams && getKebabCaseURLFromInputString(getURLStringFromInputString(colorInSearchParams))

//   if (sanitizedColorInSearchParams && allSanitizedColorSet.has(sanitizedColorInSearchParams)) {
//     className += sanitizedColorInSearchParams !== currentProductSanitizedColor ? ' d-none' : ''
//   } else {
//     className += index !== 0 ? ' d-none' : ''
//   }

//   return className
// }

interface RenderProductImagesArgs {
  productSlug: string
  productMedia: ProductMedia[]
  isWishListed?: boolean
  onWishlistToggle?: () => void
  hideWishList?: boolean
  productTitle: string
  productHierarchy?: ProductHierarchy[]
  productTags?: ProductTag[]
  hasSelectedOptionImages?: boolean
  getTopLeftIconSVG?: ({ index }: { index: number }) => ((params: any) => JSX.Element) | null
  showSmallProductImage?: boolean
  productImageClass?: string
  productImageLeftContainerClass?: string
  productImageLeftContainerSliderClass?: string
  hideProductThumbnails?: boolean
  imageContainerClassName?: string
  hideBreadCrumb?: boolean
  sliderIconClassName?: string
  breadCrumbListClass?: string
  breadCrumbContainerHeaderClass?: string
  productImageRightContainerClass?: string
  sliderImageClass?: string
  variants?: ProductVariant[]
  directory: Directory
  product: Product
  shouldLoop?: boolean
  pdpHeroImageId?: string
}

export const renderProductImages = ({
  productMedia,
  isWishListed,
  onWishlistToggle,
  productTags,
  hasSelectedOptionImages,
  hideWishList,
  getTopLeftIconSVG,
  showSmallProductImage,
  productImageClass = '',
  productImageLeftContainerClass = '',
  productImageLeftContainerSliderClass = '',
  hideProductThumbnails,
  imageContainerClassName = '',
  hideBreadCrumb,
  sliderIconClassName = '',
  breadCrumbListClass = '',
  breadCrumbContainerHeaderClass = '',
  productImageRightContainerClass = '',
  sliderImageClass = '',
  pdpHeroImageId = '',
  variants,
  directory,
  product,
}: RenderProductImagesArgs) => {
  return (
    <SliderImageWithText
      className={classNames(styles.image_container, imageContainerClassName)}
      sliderImageProps={{
        media: productMedia,
        hasSelectedOptionImages,
        leftSliderImage: productImageClass,
        leftContainerClassname: productImageLeftContainerClass,
        rightContainerClassname: productImageRightContainerClass,
        className: sliderImageClass,
        leftContainerSliderClassname: productImageLeftContainerSliderClass,
        hideProductThumbnails,
        sliderIconClassName,
        shouldLoop: false,
        pdpHeroImageId: pdpHeroImageId,
      }}
      isWishListed={isWishListed}
      onWishListToggle={onWishlistToggle}
      productTags={productTags}
      directory={directory}
      hideWishList={hideWishList || showSmallProductImage}
      getTopLeftIconSVG={getTopLeftIconSVG}
      hideBreadCrumb={hideBreadCrumb}
      breadCrumbListClass={breadCrumbListClass}
      breadCrumbContainerHeaderClass={breadCrumbContainerHeaderClass}
      variants={variants}
      product={product}
    />
  )
}

export default ProductImages
